/** SUNTRONIX ONE — usePreflightGuard hook.
 *
 *  Central helper any page can use to intercept a Preflight block coming
 *  back from a POST/PATCH and route it into the shared dialog.  The
 *  hook also logs every block + retry into the Preflight Retry Ledger
 *  so managers can see which rules chase operators most.
 *
 *  Usage:
 *      const { runGuarded, dialog } = usePreflightGuard();
 *      // wrap the API call
 *      await runGuarded({
 *          entity_kind: "shipment", entity_id: row.id, action: "dispatch",
 *          call: () => api.post(`/shipments/${row.id}/status`, {status: next}),
 *          onSuccess: reload,
 *      });
 *      // render the dialog root somewhere in the tree
 *      {dialog}
 */
import { useCallback, useState } from "react";
import { toast } from "sonner";
import PreflightBlockDialog, { logPreflightLedger }
  from "@/components/app/PreflightBlockDialog";

export function usePreflightGuard() {
  const [block, setBlock] = useState(null);
  const [lastAttempt, setLastAttempt] = useState(null);

  const runGuarded = useCallback(async (opts) => {
    const { entity_kind, entity_id, action, call, onSuccess } = opts;
    try {
      const res = await call();
      // If we had a prior block and this succeeded, log a resolution row
      if (lastAttempt && lastAttempt.entity_id === entity_id) {
        logPreflightLedger("retry_resolved", { ...lastAttempt });
        setLastAttempt(null);
      }
      onSuccess?.(res);
      return res;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "preflight_blocked") {
        const attempt = { entity_kind, entity_id, action,
                              failed_checks: detail.failed_checks || [] };
        // If this is a retry of the same entity, tag it as a re-block
        if (lastAttempt && lastAttempt.entity_id === entity_id) {
          logPreflightLedger("retry_blocked_again", attempt);
        } else {
          logPreflightLedger("block", attempt);
        }
        setLastAttempt(attempt);
        setBlock({ ...detail, entity_kind, entity_id, action });
        return null;
      }
      // Non-preflight failure — surface via toast without the raw payload
      const msg = typeof detail === "string"
        ? detail
        : detail
          ? (detail.message || JSON.stringify(detail))
          : (e?.message || "Failed");
      toast.error(msg);
      throw e;
    }
  }, [lastAttempt]);

  const retry = useCallback(async () => {
    if (!block) return;
    logPreflightLedger("retry", {
      entity_kind: block.entity_kind, entity_id: block.entity_id,
      action: block.action, failed_checks: block.failed_checks || [],
    });
    setBlock(null);
  }, [block]);

  const dialog = (
    <PreflightBlockDialog
      block={block}
      onClose={() => setBlock(null)}
      onRetry={retry}
      retryLabel="Close · then retry"
    />
  );

  return { runGuarded, dialog, block, clear: () => setBlock(null) };
}
